import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cambiarEstadoUsuario, crearUsuario,
  extraerLista, guardarRol, listarPermisosRol, listarRoles, listarRolesUsuario,
  listarUsuarios, guardarRolesUsuario, resetearClaveUsuario,
} from "../../servicios/seguridadApi";
import { obtenerSesion, PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";
import { MensajeToastError, MensajeToastExito } from "../../componentes/FuncionesLocales";
import { PASSWORD_MIN_LENGTH, PASSWORD_MIN_LENGTH_ERROR, passwordCumpleLongitudMinima } from "../../servicios/passwordPolicy";
import BotonInstructivo from "../../ComponentesGlobales/BotonInstructivo";
import "./seguridad.css";

const vacioUsuario = { Mail: "", PassWord: "", Nombre: "", Apellido: "" };
const vacioRol = { Id: 0, Codigo: "", Nombre: "", Activo: true, EsAdministrador: false };

const gruposPermisos = [
  { id: "ventas", nombre: "Ventas y clientes", icono: "pi-shopping-cart", modulos: ["Ventas", "Pedidos", "Clientes", "Cajas", "Cobros", "Facturacion", "Comprobantes"] },
  { id: "productos", nombre: "Productos y precios", icono: "pi-tags", modulos: ["Catalogos", "Mercaderias", "Precios"] },
  { id: "produccion", nombre: "Producción y trazabilidad", icono: "pi-box", modulos: ["Produccion", "Trazabilidad"] },
  { id: "reportes", nombre: "Reportes", icono: "pi-chart-bar", modulos: ["Reportes"] },
  { id: "seguridad", nombre: "Seguridad y usuarios", icono: "pi-shield", modulos: ["Seguridad", "Usuarios"] },
  { id: "configuracion", nombre: "Configuración", icono: "pi-cog", modulos: ["Configuracion"] },
];

export function agruparPermisosPorTipo(permisosDisponibles) {
  const resultado = gruposPermisos.map((grupo) => ({ ...grupo, permisos: [] }));
  const otros = { id: "otros", nombre: "Otros", icono: "pi-ellipsis-h", permisos: [] };

  permisosDisponibles.forEach((permiso) => {
    const modulo = String(permiso.Codigo || "").split(".")[0];
    const grupo = resultado.find((item) => item.modulos.includes(modulo)) || otros;
    grupo.permisos.push(permiso);
  });

  return [...resultado, otros]
    .filter((grupo) => grupo.permisos.length > 0)
    .map((grupo) => ({
      ...grupo,
      permisos: grupo.permisos.sort((a, b) =>
        String(a.Nombre || a.Codigo).localeCompare(String(b.Nombre || b.Codigo), "es")),
    }));
}

export function calcularCambiosRoles(rolesIniciales, rolesActuales) {
  const asignacionesIniciales = new Map(rolesIniciales.map((item) => [
    Number(item.Id),
    Boolean(item.Asignado),
  ]));

  return rolesActuales
    .filter((item) => asignacionesIniciales.get(Number(item.Id)) !== Boolean(item.Asignado))
    .map((item) => ({ RolId: Number(item.Id), Asignar: Boolean(item.Asignado) }));
}

export default function Seguridad() {
  const administraUsuarios = puedeAcceder(PERMISOS.USUARIOS_ADMINISTRAR);
  const administraSeguridad = puedeAcceder(PERMISOS.SEGURIDAD_ADMINISTRAR);
  const [vista, setVista] = useState(administraUsuarios ? "usuarios" : "roles");
  const [mostrarAltaUsuario, setMostrarAltaUsuario] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [usuario, setUsuario] = useState(null);
  const [rolesUsuario, setRolesUsuario] = useState([]);
  const [rolesUsuarioIniciales, setRolesUsuarioIniciales] = useState([]);
  const [rol, setRol] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [nuevoUsuario, setNuevoUsuario] = useState(vacioUsuario);
  const [nuevoRol, setNuevoRol] = useState(vacioRol);
  const [editarClave, setEditarClave] = useState(false);
  const [claveNueva, setClaveNueva] = useState("");
  const [confirmarEstado, setConfirmarEstado] = useState(false);
  const [procesando, setProcesando] = useState(false);

  const ejecutar = useCallback(async (accion, exito) => {
    setProcesando(true);
    try {
      const respuesta = await accion();
      if (respuesta?.error) {
        MensajeToastError(respuesta.error);
        return null;
      }
      if (exito) MensajeToastExito(exito);
      return respuesta;
    } finally {
      setProcesando(false);
    }
  }, []);

  const cargarUsuarios = useCallback(async () => {
    if (!administraUsuarios) return;
    const respuesta = await ejecutar(() => listarUsuarios(busquedaAplicada, false));
    if (respuesta) setUsuarios(extraerLista(respuesta));
  }, [administraUsuarios, busquedaAplicada, ejecutar]);

  const cargarRoles = useCallback(async () => {
    if (!administraSeguridad) return;
    const respuesta = await ejecutar(listarRoles);
    if (respuesta) setRoles(extraerLista(respuesta));
  }, [administraSeguridad, ejecutar]);

  useEffect(() => { cargarUsuarios(); }, [cargarUsuarios]);
  useEffect(() => { cargarRoles(); }, [cargarRoles]);

  const seleccionarUsuario = async (seleccionado) => {
    setEditarClave(false);
    setClaveNueva("");
    setConfirmarEstado(false);
    setUsuario(seleccionado);
    const respuesta = await ejecutar(() => listarRolesUsuario(seleccionado.OperadorId));
    if (respuesta) {
      const lista = extraerLista(respuesta);
      setRolesUsuario(lista);
      setRolesUsuarioIniciales(lista);
    }
  };

  const cerrarUsuario = () => {
    if (procesando) return;
    setUsuario(null);
    setRolesUsuario([]);
    setRolesUsuarioIniciales([]);
    setEditarClave(false);
    setClaveNueva("");
    setConfirmarEstado(false);
  };

  const seleccionarRol = async (seleccionado) => {
    setRol(seleccionado);
    setNuevoRol(seleccionado);
    setPermisos([]);
    const respuesta = await ejecutar(() => listarPermisosRol(seleccionado.Id));
    if (respuesta) {
      const lista = extraerLista(respuesta);
      setPermisos(lista);
    }
  };

  const altaUsuario = async (event) => {
    event.preventDefault();
    if (!passwordCumpleLongitudMinima(nuevoUsuario.PassWord)) {
      MensajeToastError(PASSWORD_MIN_LENGTH_ERROR);
      return;
    }
    const respuesta = await ejecutar(() => crearUsuario(nuevoUsuario), "Usuario creado correctamente.");
    if (!respuesta) return;
    setNuevoUsuario(vacioUsuario);
    setMostrarAltaUsuario(false);
    await cargarUsuarios();
    const OperadorId = Number(respuesta.data?.OperadorId);
    const creado = { ...nuevoUsuario, OperadorId, Activo: true };
    if (OperadorId) await seleccionarUsuario(creado);
  };

  const buscarUsuarios = (event) => {
    event.preventDefault();
    const termino = busqueda.trim();
    if (termino === busquedaAplicada) cargarUsuarios();
    else setBusquedaAplicada(termino);
  };

  const limpiarBusqueda = () => {
    setBusqueda("");
    setBusquedaAplicada("");
  };

  const alternarEstado = async (item) => {
    const respuesta = await ejecutar(() => cambiarEstadoUsuario(item.OperadorId, !item.Activo), "Estado actualizado.");
    if (respuesta) {
      setConfirmarEstado(false);
      setUsuario((actual) => actual?.OperadorId === item.OperadorId ? { ...actual, Activo: !item.Activo } : actual);
      await cargarUsuarios();
    }
  };

  const resetear = async (item) => {
    if (!passwordCumpleLongitudMinima(claveNueva)) {
      MensajeToastError(PASSWORD_MIN_LENGTH_ERROR);
      return;
    }
    const respuesta = await ejecutar(() => resetearClaveUsuario(item.OperadorId, claveNueva), "Contraseña restablecida.");
    if (respuesta) {
      setEditarClave(false);
      setClaveNueva("");
    }
  };

  const alternarRolUsuario = (item) => {
    if (!usuario || !administraSeguridad) return;
    setRolesUsuario((actuales) => actuales.map((roleItem) =>
      Number(roleItem.Id) === Number(item.Id)
        ? { ...roleItem, Asignado: !roleItem.Asignado }
        : roleItem));
  };

  const guardarRolesYCerrar = async () => {
    if (!usuario || !administraSeguridad) {
      cerrarUsuario();
      return;
    }
    const Cambios = calcularCambiosRoles(rolesUsuarioIniciales, rolesUsuario);
    if (Cambios.length === 0) {
      cerrarUsuario();
      return;
    }
    const respuesta = await ejecutar(
      () => guardarRolesUsuario(usuario.OperadorId, Cambios),
      "Roles actualizados.",
    );
    if (respuesta) cerrarUsuario();
  };

  const grabarRol = async (event) => {
    event.preventDefault();
    const Permisos = permisos
      .filter((item) => Boolean(item.Asignado))
      .map((item) => item.Codigo);
    const respuesta = await ejecutar(
      () => guardarRol({ ...nuevoRol, Permisos }),
      "Rol guardado.",
    );
    if (!respuesta) return;
    setNuevoRol(vacioRol);
    setRol(null);
    setPermisos([]);
    await cargarRoles();
  };

  const alternarPermiso = (item) => {
    if (!rol) return;
    setPermisos((actuales) => actuales.map((permiso) =>
      permiso.Codigo === item.Codigo
        ? { ...permiso, Asignado: !permiso.Asignado }
        : permiso));
  };

  const crearNuevoRol = () => {
    setRol(null);
    setNuevoRol(vacioRol);
    setPermisos([]);
  };

  const esUsuarioPropio = Number(usuario?.OperadorId) === Number(obtenerSesion()?.OperadorId);
  const permisosAgrupados = useMemo(() => agruparPermisosPorTipo(permisos), [permisos]);

  return (
    <main className="seguridad" aria-busy={procesando}>
      <header className="seguridad-encabezado">
        <div className="seguridad-encabezado-principal">
          <span className="seguridad-icono" aria-hidden="true"><i className="pi pi-shield" /></span>
          <div><h1>Seguridad</h1><p>Administrá quién puede ingresar y qué puede hacer.</p></div>
        </div>
        <BotonInstructivo to="/tutorial/seguridad" sobreColor />
      </header>

      <div className="seguridad-contenido vista-entrada-lateral">
        <nav className="seguridad-tabs" aria-label="Secciones de seguridad">
          {administraUsuarios && <button type="button" className={vista === "usuarios" ? "activo" : ""} onClick={() => setVista("usuarios")}><i className="pi pi-users" /><span>Usuarios</span><small>{usuarios.length}</small></button>}
          {administraSeguridad && <button type="button" className={vista === "roles" ? "activo" : ""} onClick={() => setVista("roles")}><i className="pi pi-lock" /><span>Roles y permisos</span><small>{roles.length}</small></button>}
        </nav>

        {vista === "usuarios" && administraUsuarios && <section className="seguridad-panel" aria-labelledby="seguridad-usuarios-titulo">
          <div className="seguridad-toolbar">
            <div><h2 id="seguridad-usuarios-titulo">Usuarios</h2><p>Personas que pueden ingresar al sistema.</p></div>
            <button type="button" className="seguridad-primario" onClick={() => setMostrarAltaUsuario(true)}><i className="pi pi-plus" /> Nuevo usuario</button>
          </div>
          <form className="seguridad-busqueda" role="search" onSubmit={buscarUsuarios}>
            <label><i className="pi pi-search" aria-hidden="true" /><span className="seguridad-sr-only">Buscar usuario</span><input aria-label="Buscar usuario" placeholder="Buscar por nombre o correo" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} /></label>
            {busqueda && <button type="button" className="seguridad-limpiar" onClick={limpiarBusqueda} aria-label="Limpiar búsqueda"><i className="pi pi-times" /></button>}
            <button type="submit" className="seguridad-secundario" disabled={procesando}>Buscar</button>
          </form>

          <div className="seguridad-lista">
            {usuarios.length === 0 && <div className="seguridad-vacio"><i className="pi pi-users" /><strong>No encontramos usuarios</strong><span>Probá otra búsqueda o creá uno nuevo.</span></div>}
            {usuarios.map((item) => {
              return <article key={item.OperadorId}>
                <button type="button" className="seguridad-identidad" onClick={() => seleccionarUsuario(item)} aria-label={`Administrar acceso de ${item.Nombre} ${item.Apellido}`}>
                  <span className="seguridad-avatar" aria-hidden="true">{String(item.Nombre || item.Mail || "U").charAt(0).toUpperCase()}</span>
                  <span className="seguridad-identidad-texto"><strong>{item.Nombre} {item.Apellido}</strong><small>{item.Mail}</small></span>
                  <span className={`seguridad-estado ${item.Activo ? "activo" : "inactivo"}`}>{item.Activo ? "Activo" : "Inactivo"}</span>
                  <span className="seguridad-administrar">Administrar <i className="pi pi-chevron-right" aria-hidden="true" /></span>
                </button>
              </article>;
            })}
          </div>
        </section>}

        {vista === "roles" && administraSeguridad && <section className="seguridad-panel" aria-labelledby="seguridad-roles-titulo">
          <div className="seguridad-toolbar">
            <div><h2 id="seguridad-roles-titulo">Roles y permisos</h2><p>Un rol agrupa los permisos de un tipo de usuario.</p></div>
            <button type="button" className="seguridad-primario" onClick={crearNuevoRol}><i className="pi pi-plus" /> Nuevo rol</button>
          </div>
          <div className="seguridad-roles-layout">
            <aside className="seguridad-roles-lista" aria-label="Roles disponibles">
              <strong>Roles</strong>
              {roles.length === 0 && <div className="seguridad-vacio compacto"><span>No hay roles creados.</span></div>}
              {roles.map((item) => <button type="button" key={item.Id} className={rol?.Id === item.Id ? "seleccionado" : ""} onClick={() => seleccionarRol(item)}><span><strong>{item.Nombre}</strong><small>{item.Codigo}</small></span><i className="pi pi-chevron-right" /></button>)}
            </aside>
            <form className="seguridad-editor" onSubmit={grabarRol}>
              <div className="seguridad-editor-cabecera"><div><h3>{rol ? rol.Nombre : "Nuevo rol"}</h3><p>{rol ? "Editá sus datos y permisos." : "Completá los datos para crear el rol."}</p></div>{rol && <span className={`seguridad-estado ${nuevoRol.Activo ? "activo" : "inactivo"}`}>{nuevoRol.Activo ? "Activo" : "Inactivo"}</span>}</div>
              <div className="seguridad-campos">
                <label><span>Nombre del rol</span><input required placeholder="Ej. Encargado de ventas" value={nuevoRol.Nombre} onChange={(e) => setNuevoRol({ ...nuevoRol, Nombre: e.target.value })} /></label>
                <label><span>Código</span><input required placeholder="Ej. VENTAS" value={nuevoRol.Codigo} onChange={(e) => setNuevoRol({ ...nuevoRol, Codigo: e.target.value })} /></label>
              </div>
              <div className="seguridad-checks"><label><input type="checkbox" checked={nuevoRol.Activo} onChange={(e) => setNuevoRol({ ...nuevoRol, Activo: e.target.checked })} /><span>Rol activo</span></label><label><input type="checkbox" checked={nuevoRol.EsAdministrador} onChange={(e) => setNuevoRol({ ...nuevoRol, EsAdministrador: e.target.checked })} /><span>Es administrador</span></label></div>
              {rol && <div className="seguridad-permisos">
                <div><h4>Permisos</h4><p>Marcá lo que podrá hacer este rol, organizado por área.</p></div>
                {permisos.length === 0 && <span className="seguridad-sin-opciones">No hay permisos disponibles.</span>}
                <div className="seguridad-grupos-permisos">
                  {permisosAgrupados.map((grupo) => {
                    const asignados = grupo.permisos.filter((item) => Boolean(item.Asignado)).length;
                    return <section className="seguridad-grupo-permisos" key={grupo.id} aria-labelledby={`permisos-${grupo.id}`}>
                      <header>
                        <span className="seguridad-grupo-icono" aria-hidden="true"><i className={`pi ${grupo.icono}`} /></span>
                        <h5 id={`permisos-${grupo.id}`}>{grupo.nombre}</h5>
                        <span className="seguridad-grupo-contador">{asignados} de {grupo.permisos.length}</span>
                      </header>
                      <div className="seguridad-opciones">
                        {grupo.permisos.map((item) => <label key={item.Codigo} className={item.Asignado ? "asignado" : ""}><input type="checkbox" checked={Boolean(item.Asignado)} disabled={procesando} onChange={() => alternarPermiso(item)} /><span><strong>{item.Nombre || item.Codigo}</strong><small>{item.Codigo}</small></span></label>)}
                      </div>
                    </section>;
                  })}
                </div>
              </div>}
              {!rol && <div className="seguridad-aviso"><i className="pi pi-info-circle" /><span>Guardá el rol para poder asignarle permisos.</span></div>}
              <footer><button type="submit" className="seguridad-primario" disabled={procesando}><i className={`pi ${procesando ? "pi-spin pi-spinner" : "pi-save"}`} /> Guardar rol</button></footer>
            </form>
          </div>
        </section>}
      </div>

      {usuario && <div className="seguridad-modal-fondo" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && cerrarUsuario()}><section className="seguridad-modal seguridad-modal-usuario" role="dialog" aria-modal="true" aria-labelledby="seguridad-usuario-modal-titulo">
        <header className="seguridad-modal-usuario-cabecera">
          <div className="seguridad-modal-identidad"><span className="seguridad-avatar grande" aria-hidden="true">{String(usuario.Nombre || usuario.Mail || "U").charAt(0).toUpperCase()}</span><div><span className="seguridad-modal-etiqueta">Administrar usuario</span><h2 id="seguridad-usuario-modal-titulo">{usuario.Nombre} {usuario.Apellido}</h2><p>{usuario.Mail}</p></div></div>
          <div className="seguridad-modal-cabecera-acciones"><span className={`seguridad-estado ${usuario.Activo ? "activo" : "inactivo"}`}>{usuario.Activo ? "Activo" : "Inactivo"}</span><button type="button" onClick={cerrarUsuario} aria-label="Cerrar" disabled={procesando}><i className="pi pi-times" /></button></div>
        </header>
        <div className="seguridad-modal-usuario-cuerpo">
          <div className="seguridad-modal-seccion-titulo"><div><h3>Roles asignados</h3><p>Seleccioná los roles que necesita este usuario.</p></div><span>{rolesUsuario.filter((item) => item.Asignado).length} asignados</span></div>
          <div className="seguridad-opciones seguridad-opciones-modal">
            {rolesUsuario.length === 0 && <div className="seguridad-vacio compacto"><i className="pi pi-lock" /><span>No hay roles disponibles.</span></div>}
            {rolesUsuario.map((roleItem) => <label key={roleItem.Id} className={roleItem.Asignado ? "asignado" : ""}><input type="checkbox" checked={Boolean(roleItem.Asignado)} disabled={!administraSeguridad || procesando} onChange={() => alternarRolUsuario(roleItem)} /><span><strong>{roleItem.Nombre}</strong><small>{roleItem.Codigo}</small></span></label>)}
          </div>
          {!administraSeguridad && <div className="seguridad-aviso"><i className="pi pi-eye" /><span>Podés consultar los roles, pero no modificarlos.</span></div>}

          <div className="seguridad-cuenta">
            <div className="seguridad-cuenta-titulo"><h3>Cuenta</h3><p>Administrá la clave y el acceso de este usuario.</p></div>

            <div className={`seguridad-cuenta-fila ${editarClave ? "abierta" : ""}`}>
              <span className="seguridad-cuenta-icono"><i className="pi pi-key" /></span>
              <div className="seguridad-cuenta-info"><strong>Contraseña</strong><span>{editarClave ? `Ingresá una nueva clave de al menos ${PASSWORD_MIN_LENGTH} caracteres.` : "Creá una nueva clave de acceso."}</span></div>
              {!editarClave ? <button type="button" className="seguridad-secundario" onClick={() => { setEditarClave(true); setConfirmarEstado(false); }}>Cambiar clave</button> : <div className="seguridad-clave-editor"><input type="password" minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" placeholder="Nueva clave" aria-label="Nueva clave" value={claveNueva} onChange={(event) => setClaveNueva(event.target.value)} autoFocus /><button type="button" className="seguridad-secundario" onClick={() => { setEditarClave(false); setClaveNueva(""); }} disabled={procesando}>Cancelar</button><button type="button" className="seguridad-primario" onClick={() => resetear(usuario)} disabled={procesando || !passwordCumpleLongitudMinima(claveNueva)}>Guardar</button></div>}
            </div>

            <div className={`seguridad-cuenta-fila ${confirmarEstado ? "abierta" : ""}`}>
              <span className="seguridad-cuenta-icono"><i className={`pi ${usuario.Activo ? "pi-user-minus" : "pi-user-plus"}`} /></span>
              <div className="seguridad-cuenta-info"><strong>{usuario.Activo ? "Desactivar usuario" : "Activar usuario"}</strong><span>{confirmarEstado ? (esUsuarioPropio && usuario.Activo ? "Estás por desactivar tu propio usuario." : `¿Confirmás ${usuario.Activo ? "la desactivación" : "la activación"} de esta cuenta?`) : (usuario.Activo ? "Impedirá que vuelva a ingresar al sistema." : "Le permitirá volver a ingresar al sistema.")}</span></div>
              {!confirmarEstado ? <button type="button" className={usuario.Activo ? "seguridad-peligro" : "seguridad-secundario"} onClick={() => { setConfirmarEstado(true); setEditarClave(false); setClaveNueva(""); }}>{usuario.Activo ? "Desactivar" : "Activar"}</button> : <div className="seguridad-confirmar-acciones"><button type="button" className="seguridad-secundario" onClick={() => setConfirmarEstado(false)} disabled={procesando}>Cancelar</button><button type="button" className={usuario.Activo ? "seguridad-peligro" : "seguridad-primario"} onClick={() => alternarEstado(usuario)} disabled={procesando}>Confirmar</button></div>}
            </div>
          </div>
        </div>
        <footer className="seguridad-modal-usuario-pie"><button type="button" className="seguridad-primario" onClick={guardarRolesYCerrar} disabled={procesando}>{procesando ? "Guardando…" : "Listo"}</button></footer>
      </section></div>}

      {mostrarAltaUsuario && <div className="seguridad-modal-fondo" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !procesando && setMostrarAltaUsuario(false)}><form className="seguridad-modal" onSubmit={altaUsuario}>
        <header><div><h2>Nuevo usuario</h2><p>Creá una cuenta para ingresar al sistema.</p></div><button type="button" onClick={() => setMostrarAltaUsuario(false)} aria-label="Cerrar" disabled={procesando}><i className="pi pi-times" /></button></header>
        <div className="seguridad-modal-cuerpo"><label><span>Correo electrónico</span><input type="email" required autoComplete="email" placeholder="nombre@empresa.com" value={nuevoUsuario.Mail} onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, Mail: e.target.value })} /></label><div className="seguridad-modal-fila"><label><span>Nombre</span><input required autoComplete="given-name" value={nuevoUsuario.Nombre} onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, Nombre: e.target.value })} /></label><label><span>Apellido</span><input required autoComplete="family-name" value={nuevoUsuario.Apellido} onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, Apellido: e.target.value })} /></label></div><label><span>Clave inicial</span><input type="password" minLength={PASSWORD_MIN_LENGTH} required autoComplete="new-password" placeholder={`Mínimo ${PASSWORD_MIN_LENGTH} caracteres`} value={nuevoUsuario.PassWord} onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, PassWord: e.target.value })} /><small>El usuario podrá cambiarla después.</small></label></div>
        <footer><button type="button" className="seguridad-secundario" onClick={() => setMostrarAltaUsuario(false)} disabled={procesando}>Cancelar</button><button type="submit" className="seguridad-primario" disabled={procesando}>{procesando ? "Creando…" : "Crear usuario"}</button></footer>
      </form></div>}
    </main>
  );
}
